/**
 * The single decode boundary for the wire GameStateDto (serde-card-form migration): expands the
 * short wire form — base-32 hands, unsigned bitmasks, ordinal-keyed trick cards — into the
 * render-state shape the existing renderers already read: a hand card is `{code, suit, rank,
 * isTrump, isLegal, faceUp}`, a trick card is `{code, suit, rank, playerName, isWinner}`. That
 * keeps the ~15 direct consumers of those fields untouched (render-hand.js, render-trick.js,
 * render-piles.js, exchange-mutations.js, deal-sort.js, full-table-deal.js, ...) — only
 * render.js's opponent renderer changes, to build face-down backs from `handSize` instead of
 * iterating a hidden hand's (now absent) card array.
 *
 * Recurses into `events[].next`: each event nests a full state snapshot
 * (animation/dispatch.js#applyEvent reads it as authoritative), so it needs the same expansion
 * as the top-level state.
 *
 * The round-end story surfaces (result, ramschResult, initialHands) decode through the sibling
 * boundary `decode-round-record.js` — a separate resource (`GET /api/round-record`), fetched and
 * decoded independently of this one. `scoringSkat` stays here: it's a `GameStateDto` field.
 *
 * Display direction applies at decode (card-preferences design § A): `sortDescending()` reverses
 * every display-sorted sequence this boundary expands — an involution over the server's canonical
 * ascending order, not a re-sort, so decoded state order stays displayed order for every
 * downstream consumer. Trick cards are play order, never reversed.
 */
import { cardView, decodeSequence } from "./card-ordinal.js";
import { codesFromMask } from "./card-set.js";
import { isTrumpCard } from "../utils.js";
import { sortDescending } from "../sort-order.js";

/** Applies the sortDescending display preference in place — a plain array reversal, not a
 *  re-sort (the server already sorted; direction is a view opinion, see this file's header). */
function reverseIfDescending(cards) {
  if (sortDescending()) cards.reverse();
  return cards;
}

/** One player's serde hand fields expanded into the `cards` array render-hand.js/render.js read. */
function decodePlayer(player, gameType) {
  if (player.hand == null) return { ...player, cards: [] };
  const legalCodes = new Set(codesFromMask(player.legalMoves ?? 0));
  const cards = decodeSequence(player.hand).map((c) => ({
    ...c,
    isTrump: isTrumpCard(c, { gameType }),
    isLegal: legalCodes.has(c.code),
    faceUp: true,
  }));
  return { ...player, cards: reverseIfDescending(cards) };
}

/** A TrickDto's ordinal-keyed cards expanded into the view render-trick.js/render-piles.js read
 *  (trump stays their existing isTrumpCard derivation — untouched here, as before this slice). */
function decodeTrick(trick) {
  if (trick == null) return null;
  return {
    ...trick,
    cards: trick.cards.map((tc) => ({
      ...cardView(tc.card),
      playerName: tc.playerName,
      isWinner: tc.isWinner,
    })),
  };
}

/** Decode one wire GameStateDto into its render-state shape, recursing into events[].next. */
export function decodeWireState(wire) {
  if (wire == null) return wire;
  return {
    ...wire,
    players: (wire.players ?? []).map((p) => decodePlayer(p, wire.gameType)),
    currentTrick: decodeTrick(wire.currentTrick),
    lastCompletedTrick: decodeTrick(wire.lastCompletedTrick),
    // The two cards the declarer took into hand from the skat — empty outside the server's
    // pickup→discard exposure window (GameStateDto#buildPickedUpSkat), NOT the live skat pile.
    // exchange-mutations.js#SkatPickedUp is the only consumer.
    pickedUpSkatCodes: codesFromMask(wire.pickedUpSkat ?? 0),
    dealOrder:
      wire.dealOrder != null
        ? decodeSequence(wire.dealOrder).map((c) => c.code)
        : null,
    // The FINISHED skat pile's face cards (null off-FINISHED, and for Ramsch) —
    // render-skat.js/score-story.js read suit/rank/isTrump, same as a hand card.
    scoringSkat:
      wire.scoringSkat != null
        ? reverseIfDescending(
            decodeSequence(wire.scoringSkat).map((c) => ({
              ...c,
              isTrump: isTrumpCard(c, { gameType: wire.gameType }),
            })),
          )
        : null,
    events: (wire.events ?? []).map((e) => ({
      ...e,
      next: decodeWireState(e.next),
    })),
  };
}
